import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  NgModule,
  Optional,
  Provider,
  SkipSelf,
  provideAppInitializer,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';

import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  loginRequest,
  msalConfig,
  validateAuthConfiguration,
} from './services/auth-config';
import { AuthInterceptor } from '../interceptors/auth.interceptor';
import { AuthService } from './services/auth.service';
import { LoaderService } from './services/loader.service';
import { UserService } from './services/user.service';
import { BusinessClockService } from './services/business-clock.service';
import { FullLoadingComponent } from './components/full-loading/full-loading.component';
import { TasksService } from './services/tasks.service';
import { ToastrAbstractionService } from './services';
import { MissingTranslationService } from './services/missing-translation.service';
import { environment } from '../environments/environment';
import { ToastrModule } from 'ngx-toastr';
import { DialogComponent } from '../modules/dialog/dialog.component';
import { CustomInputComponent } from './components/custom-input/custom-input.component';
import { DropdownComponent } from './components/dropdown/dropdown.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AdminService } from './services/mspm-admin.service';
import { preloadIconFont } from './helpers/preload-icon-font';

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function MSALInstanceFactory(): IPublicClientApplication {
  validateAuthConfiguration();
  return new PublicClientApplication(msalConfig);
}

/**
 * Set your default interaction type for MSALGuard here. If we have any
 * additional scopes we want the user to consent upon login, we will add them here as well.
 */

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    // prompt=login stops Azure B2C from silently reusing its own SSO session
    // cookie. Without it, signing in after a logout resumes the previous user's
    // session and whatever credentials are entered are never evaluated.
    authRequest: { ...loginRequest, prompt: 'login' },
  };
}

const AUTH_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  multi: true,
  useClass: AuthInterceptor,
};

const MSAL_INSTANCE_PROVICER: Provider = {
  provide: MSAL_INSTANCE,
  useFactory: MSALInstanceFactory,
};

const MSAL_GUARD_CONFIG_PROVIDER: Provider = {
  provide: MSAL_GUARD_CONFIG,
  useFactory: MSALGuardConfigFactory,
};

@NgModule({
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MissingTranslationService,
      },
      defaultLanguage: environment.defaultLocale,
    }),
    ToastrModule.forRoot({
      preventDuplicates: true,
      countDuplicates: true,
      resetTimeoutOnDuplicate: true,
      includeTitleDuplicates: true,
      progressBar: true,
      progressAnimation: 'increasing',
      positionClass: 'toast-bottom-left',
    }),
    MsalModule,
    FullLoadingComponent,
    DialogComponent,
    CustomInputComponent,
    DropdownComponent,
  ],
  exports: [
    TranslateModule,
    MsalModule,
    FullLoadingComponent,
    DialogComponent,
    CustomInputComponent,
    DropdownComponent,
  ],
  providers: [
    AUTH_PROVIDER,
    MSAL_INSTANCE_PROVICER,
    MSAL_GUARD_CONFIG_PROVIDER,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    AuthService,
    UserService,
    LoaderService,
    BusinessClockService,
    TasksService,
    ToastrAbstractionService,
    AdminService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(preloadIconFont),
    // GridService,
  ],
  declarations: [],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error(
        'CoreModule is already loaded. Import it in the AppModule only',
      );
    }
  }
}
